/* BalanceSheetTab — NHF's day-one Balance Sheet.
 * Computed live from posted journal entries + current-period revenue/expense
 * (which folds into net assets via period_net_income). The fundamental
 * accounting equation Assets = Liabilities + Net Assets is shown explicitly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Scale, Loader2, RefreshCw, Calendar, CheckCircle, AlertTriangle,
  TrendingUp, TrendingDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const todayIso = () => new Date().toISOString().slice(0, 10);

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const BalanceSheetTab = () => {
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/finance/balance-sheet?as_of=${asOf}`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load balance sheet.'); }
    setLoading(false);
  }, [asOf]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;
  }

  const a = data?.assets || { rows: [], total: 0 };
  const l = data?.liabilities || { rows: [], total: 0 };
  const n = data?.net_assets || { rows: [], total: 0, recorded_total: 0, period_net_income: 0 };
  const check = data?.balance_check || { is_balanced: true, out_of_balance: 0 };

  return (
    <div className="space-y-4" data-testid="balance-sheet-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#BA1826]" /> Balance Sheet
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            What NHF owns, owes, and has accumulated as of a chosen date. Built live from posted
            journal entries plus current-year revenue and expenses. Every grant or donation logged
            in Journal Entries — and every Stripe-confirmed payment — flows through here.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 border border-[#E5E7EB] rounded px-2 py-1 bg-white">
            <Calendar className="w-3 h-3 text-[#BA1826]" />
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
              className="text-xs outline-none" data-testid="balance-sheet-as-of" />
          </div>
          <button onClick={load}
            className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
            data-testid="balance-sheet-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded">{error}</div>
      )}

      {/* Balance-check banner */}
      <div className={`rounded-lg p-3 border ${check.is_balanced
        ? 'bg-green-50 border-green-200 text-green-900'
        : 'bg-amber-50 border-amber-200 text-amber-900'}`}
        data-testid="balance-sheet-equation">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            {check.is_balanced
              ? <CheckCircle className="w-4 h-4 text-green-700" />
              : <AlertTriangle className="w-4 h-4 text-amber-700" />}
            <p className="text-xs font-bold">
              {check.is_balanced ? 'Books are balanced.' : `Books are off by ${fmtMoney(check.out_of_balance)}`}
            </p>
          </div>
          <p className="text-xs font-mono">
            Assets <span className="font-bold">{fmtMoney(check.assets)}</span>
            <span className="mx-1.5 opacity-60">=</span>
            L + NA <span className="font-bold">{fmtMoney(check.liabilities_plus_net_assets)}</span>
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Section title="Assets" subtitle="What NHF owns" rows={a.rows} total={a.total}
          color="blue" testid="bs-assets" emptyText="No asset balances yet." />
        <Section title="Liabilities" subtitle="What NHF owes" rows={l.rows} total={l.total}
          color="purple" testid="bs-liabilities" emptyText="No liabilities recorded." />
        <NetAssetsSection na={n} />
      </div>

      <p className="text-[10px] text-[#9CA3AF] leading-relaxed max-w-2xl">
        <span className="font-bold">How this is built:</span> Asset balances = Σ(debit) − Σ(credit) on
        posted journal entries. Liability and Net Asset balances = Σ(credit) − Σ(debit). Period Net
        Income = revenue (journal entries + Stripe paid donations + shop orders) minus expenses
        (journal entries + recorded expenses) since {data?.period_start || 'period start'}. Period
        Net Income flows into Net Assets so the fundamental equation always holds.
      </p>
    </div>
  );
};

const COLOR_BAR = {
  blue: 'bg-blue-50 border-blue-200 text-blue-900',
  purple: 'bg-purple-50 border-purple-200 text-purple-900',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
};

const Section = ({ title, subtitle, rows, total, color, testid, emptyText }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={testid}>
    <div className={`px-3 py-2 border-b ${COLOR_BAR[color]}`}>
      <p className="text-xs font-bold uppercase tracking-wider">{title}</p>
      <p className="text-[10px] opacity-80">{subtitle}</p>
    </div>
    {rows.length === 0 ? (
      <p className="px-3 py-6 text-xs italic text-[#9CA3AF] text-center">{emptyText}</p>
    ) : (
      <table className="w-full text-xs">
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className="border-t border-[#E5E7EB]">
              <td className="px-3 py-1.5 font-mono font-bold w-14">{r.code}</td>
              <td className="px-3 py-1.5">{r.name}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold whitespace-nowrap">
                {fmtMoney(r.balance)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#0A0A0A] text-white">
            <td className="px-3 py-1.5 font-bold uppercase tracking-wider text-[10px]" colSpan={2}>
              Total {title}
            </td>
            <td className="px-3 py-1.5 text-right font-mono font-bold">{fmtMoney(total)}</td>
          </tr>
        </tfoot>
      </table>
    )}
  </div>
);

const NetAssetsSection = ({ na }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="bs-net-assets">
    <div className={`px-3 py-2 border-b ${COLOR_BAR.indigo}`}>
      <p className="text-xs font-bold uppercase tracking-wider">Net Assets</p>
      <p className="text-[10px] opacity-80">Recorded balances + current-year change</p>
    </div>
    {na.rows.length === 0 && na.period_net_income === 0 ? (
      <p className="px-3 py-6 text-xs italic text-[#9CA3AF] text-center">
        No net asset balances yet. Use Opening Balances to set day-one values.
      </p>
    ) : (
      <table className="w-full text-xs">
        <tbody>
          {na.rows.map((r) => (
            <tr key={r.code} className="border-t border-[#E5E7EB]">
              <td className="px-3 py-1.5 font-mono font-bold w-14">{r.code}</td>
              <td className="px-3 py-1.5">{r.name}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold whitespace-nowrap">
                {fmtMoney(r.balance)}
              </td>
            </tr>
          ))}
          {na.rows.length > 0 && (
            <tr className="border-t border-[#E5E7EB] bg-[#FAFBFC]">
              <td className="px-3 py-1.5 italic text-[#9CA3AF] text-[11px]" colSpan={2}>
                Subtotal recorded
              </td>
              <td className="px-3 py-1.5 text-right font-mono italic text-[#9CA3AF] whitespace-nowrap">
                {fmtMoney(na.recorded_total)}
              </td>
            </tr>
          )}
          <tr className="border-t border-[#E5E7EB] bg-emerald-50">
            <td className="px-3 py-1.5" colSpan={2}>
              <span className="inline-flex items-center gap-1 text-emerald-800">
                {na.period_net_income >= 0
                  ? <TrendingUp className="w-3 h-3" />
                  : <TrendingDown className="w-3 h-3" />}
                <span className="font-semibold">Period net income</span>
              </span>
              <p className="text-[9px] text-emerald-700/80 ml-4">
                Revenue {fmtMoney(na.period_revenue)} − Expenses {fmtMoney(na.period_expenses)}
              </p>
            </td>
            <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">
              {fmtMoney(na.period_net_income)}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr className="bg-[#0A0A0A] text-white">
            <td className="px-3 py-1.5 font-bold uppercase tracking-wider text-[10px]" colSpan={2}>
              Total Net Assets
            </td>
            <td className="px-3 py-1.5 text-right font-mono font-bold whitespace-nowrap">
              {fmtMoney(na.total)}
            </td>
          </tr>
        </tfoot>
      </table>
    )}
  </div>
);

export default BalanceSheetTab;
